import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type components } from "@njbsoft/types";
import { useTestingCompany, useUpdateTestingCompany } from "@/hooks/use-testing-company";
import { useTesters } from "@/hooks/use-testers";
import { useTestKits, type TestKit } from "@/hooks/use-test-kits";
import { useInlineField } from "@/hooks/use-inline-field";
import { useRecentPageTitle } from "@/hooks/use-recent-page-title";
import { useToast } from "@/hooks/use-toast";
import { useAppStore } from "@/store";
import { usePermissions } from "@/hooks/use-permissions";
import { CompanyTestersTable } from "@/components/testing-companies/company-testers-table";
import { CompanyOverviewTab } from "@/components/testing-companies/company-overview-tab";
import { CompanyStatusControl } from "@/components/testing-companies/company-status-control";
import { CompanyLogo } from "@/components/testing-companies/company-logo";
import { EntityDocuments } from "@/components/documents/entity-documents";
import { NotesCard } from "@/components/entity-notes/notes-card";
import { useCompanyKitsColumns } from "@/components/testing-companies/company-kits-columns";
import { TestKitCreateDrawer } from "@/components/test-kits/test-kit-create-drawer";
import { testerStatusVariants } from "@/components/testers/testers-columns";
import {
  PageHeader,
  Badge,
  Breadcrumbs,
  Button,
  DataTable,
  EmptyState,
  PageSkeleton,
  SkeletonTable,
  Tabs,
  type TabDef,
} from "@/components/ui";

type TestingCompanyUpdate = components["schemas"]["TestingCompanyUpdate"];

type CompanyTab = "overview" | "roster" | "kits" | "documents";

const ALLOWED_TABS: CompanyTab[] = ["overview", "roster", "kits", "documents"];

interface CompanyDetailSearch {
  tab?: CompanyTab;
}

function parseTab(value: unknown): CompanyTab | undefined {
  if (typeof value !== "string") return undefined;
  return ALLOWED_TABS.find((t) => t === value);
}

export const Route = createFileRoute("/_authenticated/testing-companies/$id/")({
  component: TestingCompanyDetailPage,
  validateSearch: (s: Record<string, unknown>): CompanyDetailSearch => ({
    tab: parseTab(s.tab),
  }),
});

// Same rendering as the list's date columns, so a header timestamp and a list
// cell never disagree on format.
function formatDate(value: string): string {
  return new Date(value).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/** One labelled edge timestamp in the header meta row; nothing when unset. */
function EdgeTimestamp({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  // One text node, not a label/value pair: a screen reader reads the label and
  // the date as one phrase rather than two disconnected fragments.
  return <span className="text-sm text-muted">{`${label}: ${formatDate(value)}`}</span>;
}

function KitsTab({ companyId, companyName }: { companyId: string; companyName: string }) {
  const { t } = useTranslation("test-kits");
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { success: toastSuccess } = useToast();
  const canCreate = can("test_kits", "create");
  const [createOpen, setCreateOpen] = useState(false);
  const { data, isLoading } = useTestKits({ owner_company_id: companyId });
  const columns = useCompanyKitsColumns();
  const kits = data?.items ?? [];
  // Exactly one create affordance per state: the header CTA rides the table,
  // the empty list's EmptyState carries the action, loading has neither.
  const showHeaderCta = canCreate && !isLoading && kits.length > 0;

  const handleRowClick = (row: TestKit) => {
    void navigate({ to: "/test-kits/$id", params: { id: row.id } });
  };

  return (
    <div className="space-y-4">
      {showHeaderCta && (
        <div className="flex justify-end">
          <Button onClick={() => setCreateOpen(true)}>{t("list.newCta")}</Button>
        </div>
      )}

      {isLoading ? (
        <SkeletonTable columns={5} rows={3} />
      ) : kits.length === 0 ? (
        <EmptyState
          title={t("list.empty")}
          description={t("list.emptyDescription")}
          actionLabel={canCreate ? t("list.newCta") : undefined}
          onAction={canCreate ? () => setCreateOpen(true) : undefined}
        />
      ) : (
        <DataTable columns={columns} data={kits} onRowClick={handleRowClick} />
      )}

      {canCreate && (
        <TestKitCreateDrawer
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          context={{ kind: "company", companyId, companyName }}
          onCreated={() => toastSuccess("test-kits:create.success")}
        />
      )}
    </div>
  );
}

/**
 * Testing-company detail — four tabs (Overview / Roster / Kits / Documents)
 * driven by the `?tab=` search param. Overview is the read-only profile the
 * company maintains through its own portal, plus this utility's own edge
 * fields; Roster and Kits embed their tables with their create CTAs.
 *
 * The company row is GLOBAL and this org reads it through its own
 * authorization edge, so the header carries that edge: its status badge, the
 * three edge timestamps, and the verbs that move it. The company's identity
 * (name, contact, mailing, insurance, licenses) is the company's own in
 * every state — the header name is read-only and the claimed records carry
 * the managed-by chip. The one PATCH this page issues is the edge's own
 * billing-bridge note, behind the testing_companies:update grant.
 *
 * The page's one letter door lives on the Overview tab, in the heading row of
 * the Letters card that lists the letters sent to the company; the header
 * carries the edge's status control alone. A revoked company still renders — a
 * company is never deleted — and still lists its letters, but offers no door.
 */
function TestingCompanyDetailPage() {
  const { t } = useTranslation(["testing-companies", "common", "documents", "field-credentials"]);
  const navigate = useNavigate();
  const { id } = Route.useParams();
  const { tab } = Route.useSearch();
  const orgId = useAppStore((s) => s.activeOrgId);
  const { can } = usePermissions();
  const canEdit = can("testing_companies", "update");

  const { data: company, isLoading, isError } = useTestingCompany(id);
  useRecentPageTitle(company?.name ?? undefined);
  const updateMutation = useUpdateTestingCompany(id);
  const patchField = useInlineField<TestingCompanyUpdate>(
    (body) => updateMutation.mutateAsync(body),
    { successKey: "testing-companies:edit.success" },
  );

  // Count-only probes for the tab pills (single row / shared cache, server
  // total). Overview / Documents get NO pill — no cheap count exists there
  // today, and none is added for a pill.
  const rosterCountQuery = useTesters({ testing_company_id: id, limit: 1 });
  const kitsCountQuery = useTestKits({ owner_company_id: id, limit: 1 });

  // Re-validated at read time, not just in validateSearch, so an unknown tab in
  // a hand-typed or stale URL still lands on the default body.
  const activeTab: CompanyTab = parseTab(tab) ?? "overview";
  const setActiveTab = (next: CompanyTab) => {
    void navigate({
      to: "/testing-companies/$id",
      params: { id },
      search: next === "overview" ? {} : { tab: next },
      replace: true,
    });
  };

  if (isLoading) {
    return (
      <div className="p-8">
        <PageSkeleton />
      </div>
    );
  }

  if (isError || !company) {
    return (
      <div className="p-8">
        <Breadcrumbs
          items={[{ label: t("list.title"), to: "/testers", search: { tab: "companies" } }]}
        />
        <PageHeader title={t("errors.loadDetailFailed")} />
      </div>
    );
  }

  // The reason the utility gave when it suspended or revoked this edge; the
  // wire carries NULL while the edge is pending or active.
  const statusReason =
    (company.status === "suspended" || company.status === "revoked") && company.suspension_reason
      ? company.suspension_reason
      : null;

  const tabs: TabDef<CompanyTab>[] = [
    { value: "overview", label: t("tabs.overview") },
    {
      value: "roster",
      label: t("tabs.roster"),
      count: rosterCountQuery.data?.total_count,
    },
    {
      value: "kits",
      label: t("tabs.kits"),
      count: kitsCountQuery.data?.total_count,
    },
    { value: "documents", label: t("tabs.documents") },
  ];

  return (
    <div className="p-8">
      <Breadcrumbs
        items={[
          { label: t("list.title"), to: "/testers", search: { tab: "companies" } },
          { label: company.name },
        ]}
      />
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            <CompanyLogo companyId={id} companyName={company.name} hasLogo={company.has_logo} />
            <span className="flex flex-wrap items-center gap-2">
              <span>{company.name}</span>
              {company.claimed && (
                <Badge variant="default">{t("detail.managedByCompanyChip")}</Badge>
              )}
            </span>
          </span>
        }
        meta={
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-3">
              <Badge variant={testerStatusVariants[company.status]}>
                {t(`status.${company.status}`)}
              </Badge>
              <EdgeTimestamp label={t("detail.requestedAt")} value={company.requested_at} />
              <EdgeTimestamp label={t("detail.authorizedAt")} value={company.authorized_at} />
              <EdgeTimestamp
                label={t("detail.statusChangedAt")}
                value={company.status_changed_at}
              />
            </div>
            {statusReason && (
              <p className="text-sm text-muted">
                {t("detail.statusReason", { reason: statusReason })}
              </p>
            )}
          </div>
        }
        actions={<CompanyStatusControl company={company} />}
      />

      <div className="mt-4">
        <Tabs
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          ariaLabel={company.name}
        />
        <div className="mt-6" role="tabpanel">
          {activeTab === "overview" && (
            // The Internal Notes card sits below the overview's field cards
            // (the same band the shared custom-fields card occupies). An
            // archived company's notes still list; appends reject server-side
            // with the typed 409.
            <div className="space-y-6">
              <CompanyOverviewTab company={company} canEdit={canEdit} patchField={patchField} />
              <NotesCard parent="testing_company" parentId={id} />
            </div>
          )}
          {activeTab === "roster" && <CompanyTestersTable companyId={id} />}
          {activeTab === "kits" && <KitsTab companyId={id} companyName={company.name} />}
          {activeTab === "documents" && orgId && (
            <div>
              <EntityDocuments entityType="testing_company" entityId={id} orgId={orgId} />
              <p className="mt-2 text-xs text-muted">
                {t("field-credentials:submittedEvidence.documentsProvenance")}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
